import { useState } from "react";
import { Save, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { useToast } from "@/components/ui/Toast";
import {
  addKnowledge,
  listKnowledge,
  type KnowledgeDoc,
  type KnowledgeSourceType,
} from "@/lib/api";
import { SOURCE_LABEL } from "./index";

interface AddDocFormProps {
  onCancel: () => void;
  onAdded: (doc: KnowledgeDoc) => void;
}

const SOURCE_OPTIONS: KnowledgeSourceType[] = [
  "faq",
  "rates",
  "rules",
  "policies",
  "pet_policy",
  "cancellation_policy",
  "check_in_info",
  "reservation_info",
  "amenities",
  "description",
  "location",
  "other",
];

/**
 * Inline editor for new knowledge documents. Saves through add_knowledge, then
 * re-fetches the freshly-inserted doc so the parent list can prepend the full
 * row (the add_knowledge response doesn't include timestamps).
 */
export function AddDocForm({ onCancel, onAdded }: AddDocFormProps) {
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [sourceType, setSourceType] = useState<KnowledgeSourceType>("faq");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const t = title.trim();
    const c = content.trim();
    if (!t) {
      toast.push("Add a topic before saving.", "error");
      return;
    }
    if (!c) {
      toast.push("Tell your bot what it should know before saving.", "error");
      return;
    }
    setSaving(true);
    try {
      const result = await addKnowledge({
        title: t,
        content: c,
        source_type: sourceType,
      });

      // add_knowledge returns the bare doc; refetch the full row (with
      // timestamps + is_active) so the list shows accurate metadata.
      const list = await listKnowledge();
      const fresh = list.items.find((d) => d.id === result.document.id);
      if (fresh) {
        onAdded(fresh);
      } else {
        // Fallback if something raced; build a minimal row from what we know.
        onAdded({
          id: result.document.id,
          title: t,
          source_type: sourceType,
          source_url: null,
          raw_content: c,
          is_active: true,
          is_manual: true,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
    } catch (e) {
      toast.push(e instanceof Error ? e.message : "Failed to save", "error");
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="border-b border-line px-5 py-3">
        <p className="text-sm font-medium text-text-strong">New knowledge entry</p>
        <p className="mt-0.5 text-xs text-text-soft">
          Teach your bot something new. As soon as you save, it can use this when guests ask.
        </p>
      </div>

      <div className="space-y-3 px-5 py-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <label className="block">
            <span className="text-xs font-medium text-text-strong">Topic</span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. 4th of July weekend hours"
              className="mt-1.5 block w-full rounded border border-line bg-surface px-3 py-2 text-sm text-text-strong outline-none focus:border-coral"
              disabled={saving}
            />
          </label>

          <label className="block">
            <span className="text-xs font-medium text-text-strong">Category</span>
            <select
              value={sourceType}
              onChange={(e) => setSourceType(e.target.value as KnowledgeSourceType)}
              disabled={saving}
              className="mt-1.5 block w-full rounded border border-line bg-surface px-3 py-2 text-sm text-text-strong outline-none focus:border-coral sm:w-44"
            >
              {SOURCE_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {SOURCE_LABEL[s]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <label className="block">
          <span className="text-xs font-medium text-text-strong">What your bot should know</span>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={8}
            placeholder="Write it the way you'd tell a guest. Plain sentences work best."
            disabled={saving}
            className="mt-1.5 block w-full resize-y rounded border border-line bg-surface px-3 py-2 text-sm text-text-strong outline-none focus:border-coral"
          />
          <span className="mt-1 block text-2xs text-text-soft">
            Your bot can use this the moment you save — no extra setup.
          </span>
        </label>

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={saving}
            iconLeft={<X size={12} strokeWidth={1.75} />}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={save}
            loading={saving}
            iconLeft={!saving && <Save size={12} strokeWidth={1.75} />}
          >
            {saving ? "Saving…" : "Teach the bot"}
          </Button>
        </div>
      </div>
    </div>
  );
}
